import { router, useLocalSearchParams, useNavigation } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { ScrollView, View } from "react-native";

import { CardComparePrices } from "@/components/browse/CardComparePrices";
import { withReason } from "@/components/browse/PickButton";
import { PickControl } from "@/components/browse/PickControl";
import { PublicBrowseSkeleton } from "@/components/browse/PublicBrowseSkeleton";
import { PublicCardSummary } from "@/components/browse/PublicCardSummary";
import { PublicDetailHeader } from "@/components/browse/PublicDetailHeader";
import { PullSheetBar } from "@/components/pull-sheet/PullSheetBar";
import { PullSheetErrorToast } from "@/components/pull-sheet/PullSheetErrorToast";
import { PullSheetReviewModal } from "@/components/pull-sheet/PullSheetReviewModal";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Screen } from "@/components/ui/Screen";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import { usePublicBrowseItemDetail } from "@/hooks/usePublicBrowse";
import { usePullSheet } from "@/hooks/usePullSheet";
import { loadShowAnalytics } from "@/lib/browse/showAnalyticsClient";
import { tablePath } from "@/lib/shop/publicUrls";
import { cn } from "@/lib/ui/cn";

// The browse list's desktop content cap (#469), so this page stays centered and narrow on the web.
const MAX_CONTENT_W = 640;

/**
 * The Pull Sheet item detail page (#341, ADR-0017) at `/browse/<event public_id>/<item public_id>`,
 * opened from the customer browse list. It shows the card hero (#760), the tenant's price as the
 * headline (the same server value as the list, #344), last-solds from outside marketplaces, and
 * picking.
 *
 * Last-solds come from the public `GET /events/{event}/browse/{item}` endpoint
 * (`usePublicBrowseItemDetail`), because the Dossier route requires an authenticated tenant. The
 * item row is tenant-scoped through `public_uow` RLS, while last-solds are global catalog data
 * (ADR-0003); see `services_public.browse_item_detail`. "TCGplayer market" is the `PricingSource`
 * quote from #344; the sale rows carry the marketplace label the sales feed provides
 * (`adapters/sales_history.py`), never an invented one.
 *
 * Like `browse/[eventId].tsx`, this route is a sibling of `(app)`, so no auth provider mounts.
 */
export default function BrowseItemDetailScreen() {
  // `slug` is present only under `/shop/<slug>/table/<eventId>/<itemId>` (TRO-1187). The same
  // screen also answers at `/browse/<eventId>/<itemId>`, which printed label QR codes redirect to
  // and which therefore keeps working; there the slug is absent and the way back stays the path
  // the customer arrived on, rather than moving them to a URL they did not come from.
  const { eventId, itemId, slug } = useLocalSearchParams<
    { eventId: string; itemId: string; slug?: string; }
  >();
  const { data, isLoading, error, refetch } = usePublicBrowseItemDetail(
    eventId ?? null,
    itemId ?? null,
  );
  // Also a shareable link, so it can be a cold launch. An error counts as settled, since the
  // not-found copy is readable.
  useMarkInteractive(!isLoading);
  const tableHref = slug ? tablePath(slug, eventId) : `/browse/${eventId}`;
  // Whether the table is beneath this card in the storefront's own stack. `router.canGoBack()` is
  // no guide: on a cold open (a label QR scan) the root stack still holds the app's index under
  // this route, so `back()` left the storefront for the vendor sign-in.
  const fromTable = (useNavigation().getState()?.index ?? 0) > 0;
  const cold = !fromTable;

  // Picking (#461) through the same `usePullSheet(eventId)` as the browse list, so picks persist to
  // the server and stay in sync with the list's count and subtotal.
  const pullSheet = usePullSheet(eventId ?? null, {
    showPublicId: data?.show_public_id ?? undefined,
    participationId: data?.participation_id ?? undefined,
  });
  const [reviewOpen, setReviewOpen] = useState(false);
  const count = pullSheet.sheet?.items.reduce((total, item) => total + item.quantity, 0) ?? 0;
  const subtotal = pullSheet.sheet?.subtotal ?? "0";

  // The second step of the table QR funnel after `table_qr_opened` (TRO-642).
  //
  // Fires once `data` loads, since an item that never loaded was not viewed, with the same
  // once-per-mount ref guard as the parent route so React's development double effects do not
  // double-count. It lives here rather than in the list's `openItem` so shared-link cold launches
  // count too.
  const itemOpenTracked = useRef(false);
  useEffect(() => {
    if (!data || !eventId || itemOpenTracked.current) return;
    itemOpenTracked.current = true;
    // Same Show scoping as the parent route (ADR-0106): passing `show_public_id` stops the visit
    // from reusing another Show's id.
    void loadShowAnalytics({
      showId: data.show_public_id ?? null,
      showEndsAt: null,
    }).then((analytics) => {
      analytics.browseItemOpened(eventId, {
        stockItemPublicId: data.item.public_id,
        hasPrice: data.item.price != null,
        // True when any grade scope has sales. The comps block below shows only the subject
        // scope's sales, so the two can differ.
        hasComps: data.last_solds?.scopes.some((scope) => scope.sales.length > 0) ?? false,
      });
    });
  }, [data, eventId]);

  // The storefront skeleton, not a spinner, since a label QR cold-launches here. The `"detail"`
  // layout matches this page, so content does not jump when it loads.
  if (isLoading) return <PublicBrowseSkeleton layout="detail" />;

  if (error || !data) {
    return (
      // Keep the `main` landmark in the error state (#531). A cold-launched link needs actions
      // here, matching the list's error state.
      <Screen role="main" accessibilityLiveRegion="assertive">
        <EmptyState
          mark
          title="Couldn't load this card"
          subtitle={error?.message ?? "Couldn't load this item."}
          action={
            <View className="items-center gap-2">
              <Button
                title="Try again"
                accessibilityLabel="Try again"
                variant="outline"
                onPress={() => refetch()}
              />
              <Button
                title="Back to this table"
                accessibilityLabel="Back to this table"
                variant="ghost"
                // `replace`, not `back`: a cold-launched shared link has nothing behind it.
                onPress={() => router.replace(tableHref)}
              />
            </View>
          }
        />
      </Screen>
    );
  }

  const { item } = data;
  const picked = pullSheet.pickedQty(item.public_id);
  // Label the headline by its pricing mode (#344), so an Asking price above the TCGplayer comp
  // reads as deliberate.
  const priceLabel = data.price_visibility === "ask" ? "Asking price" : "Market price";
  return (
    <Screen testID="browse-detail-screen">
      {/* This screen sets no per-card `<title>` for the tab or link preview. */}
      {
        /* Full-bleed background with capped, centered content. `role="main"` gives screen readers a
        `main` landmark, which react-native-web does not render by default (#531). */
      }
      <View className="w-full flex-1 self-center" role="main" style={{ maxWidth: MAX_CONTENT_W }}>
        {
          /* A scanned label QR cold-launches here with nothing behind it, so `back()` alone left
          the customer stuck on one card. With no history the button goes to the table and says
          so; from the table it is a plain Back. */
        }
        <PublicDetailHeader
          title={item.product_name}
          cold={cold
            ? { label: "Whole table", accessibilityLabel: "See everything at this table" }
            : undefined}
          onBack={() => (fromTable ? router.back() : router.replace(tableHref))}
          testID="browse-detail-back"
        />

        <ScrollView
          className="flex-1"
          contentContainerClassName={cn("gap-4 px-4 pt-3", count > 0 ? "pb-28" : "pb-10")}
        >
          {
            /* The vendor's own price leads (TRO review): the art, what it is, the price and the
              action, then the market comps one tap down under "Compare prices". Comps first read
              as four prices before this vendor's, and a buyer could not tell which one they pay.
              The action sits below the price so a small screen shows the price before committing:
              a labeled full-width button, then `PickControl` (undo, or a stepper for several
              copies) once picked. */
          }
          <PublicCardSummary
            detail={data}
            priceLabel={priceLabel}
            testIDPrefix="browse-detail"
            action={picked === 0
              ? (
                <Button
                  title="Add to my list"
                  onPress={() => pullSheet.setPickQuantity(item.public_id, 1)}
                  disabled={pullSheet.isPickPending(item.public_id)
                    || pullSheet.pickLockedReason != null}
                  // The reason goes in the label too, since react-native-web drops
                  // `accessibilityHint` (see `PickButton`).
                  accessibilityLabel={withReason("Add to my list", pullSheet.pickLockedReason)}
                  accessibilityHint={pullSheet.pickLockedReason ?? undefined}
                />
              )
              : (
                <PickControl
                  productName={item.product_name}
                  quantity={picked}
                  available={item.qty}
                  onChange={(n) => pullSheet.setPickQuantity(item.public_id, n)}
                  disabled={pullSheet.isPickPending(item.public_id)}
                  lockedReason={pullSheet.pickLockedReason}
                />
              )}
          />

          <CardComparePrices detail={data} />
        </ScrollView>

        {
          /* The browse list's running-count bar and review modal (#461), so the list can be
           reviewed and sent from here. */
        }
        <PullSheetBar
          count={count}
          subtotal={subtotal}
          phase={data.event_phase ?? "live"}
          inLine={pullSheet.isPresented}
          queuePosition={pullSheet.sheet?.queue_position ?? null}
          queueTotal={pullSheet.sheet?.queue_total ?? null}
          onPress={() => {
            pullSheet.clearActionError();
            setReviewOpen(true);
          }}
        />
        {
          /* As in the browse list (#360), a failed pick shows as a toast while the modal is closed;
            the modal shows its own errors inline, so this hides while it is open. */
        }
        <PullSheetErrorToast
          message={reviewOpen ? null : pullSheet.actionError}
          onDismiss={pullSheet.clearActionError}
        />
        <PullSheetReviewModal
          // Opens itself on a settle, as in the list. The detail payload has no shop name, so the
          // confirmation says "The Vendor".
          visible={reviewOpen || pullSheet.justSettled}
          phase={data.event_phase ?? "live"}
          onClose={() => {
            setReviewOpen(false);
            pullSheet.clearActionError();
            // Reset the withdraw success flag with the modal (TRO-674), so a new list does not show
            // the old "you're out of the line" message.
            pullSheet.withdraw.reset();
            pullSheet.acknowledgeSettled();
          }}
          justSettled={pullSheet.justSettled}
          sheet={pullSheet.sheet}
          onSetQuantity={(stockItemPublicId, quantity) =>
            pullSheet.setPickQuantity(stockItemPublicId, quantity)}
          onPresent={(customerLabel) => pullSheet.present.mutate(customerLabel)}
          isPresenting={pullSheet.present.isPending}
          onWithdraw={() => pullSheet.withdraw.mutate()}
          isWithdrawing={pullSheet.withdraw.isPending}
          justWithdrew={pullSheet.withdraw.isSuccess}
          error={pullSheet.actionError}
        />
      </View>
    </Screen>
  );
}
